import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    collection,
    getDocs,
    addDoc,
    deleteDoc,
    doc,
    query,
    orderBy,
    serverTimestamp,
} from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './BizSpotlight.css';

type Category = 'tip' | 'announcement' | 'feature' | 'challenge';

interface BarkBoardPost {
    id: string;
    title: string;
    body: string;
    category: Category;
    imageUrl: string | null;
    createdAt: Timestamp;
    createdBy: string;
}

export const BizSpotlight: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();

    const [posts, setPosts] = useState<BarkBoardPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [category, setCategory] = useState<Category>('tip');
    const [imageUrl, setImageUrl] = useState('');

    useEffect(() => {
        loadPosts();
    }, []);

    const loadPosts = async () => {
        try {
            setLoading(true);
            setError('');
            const q = query(collection(db, 'barkBoard'), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            const loaded: BarkBoardPost[] = snap.docs.map((d) => ({
                id: d.id,
                ...(d.data() as Omit<BarkBoardPost, 'id'>),
            }));
            setPosts(loaded);
        } catch (err: any) {
            setError(err.message || 'Failed to load posts');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || !body.trim()) return;
        if (!currentUser) return;

        try {
            setSubmitting(true);
            await addDoc(collection(db, 'barkBoard'), {
                title: title.trim(),
                body: body.trim(),
                category,
                imageUrl: imageUrl.trim() || null,
                createdAt: serverTimestamp(),
                createdBy: currentUser.uid,
            });
            setTitle('');
            setBody('');
            setCategory('tip');
            setImageUrl('');
            setFormOpen(false);
            await loadPosts();
        } catch (err: any) {
            setError(err.message || 'Failed to create post');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (postId: string) => {
        if (!window.confirm('Delete this post? This cannot be undone.')) return;
        try {
            await deleteDoc(doc(db, 'barkBoard', postId));
            setPosts((prev) => prev.filter((p) => p.id !== postId));
        } catch (err: any) {
            setError(err.message || 'Failed to delete post');
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (err) {
            console.error('Sign out failed:', err);
        }
    };

    const formatDate = (ts: Timestamp) => {
        if (!ts?.toDate) return '';
        return ts.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const truncate = (text: string | undefined, max: number) => {
        if (!text) return '';
        return text.length > max ? `${text.substring(0, max)}...` : text;
    };

    return (
        <div className="biz-spotlight-container">
            <header className="biz-spotlight-header">
                <div className="biz-header-content">
                    <div>
                        <h1>✨ Biz Spotlight</h1>
                        <p className="biz-header-subtitle">Post to biz.omore.app Spotlight</p>
                    </div>
                    <div className="biz-header-actions">
                        <button onClick={() => navigate('/dashboard')} className="biz-back-button">
                            ← Back to Dashboard
                        </button>
                        <span className="biz-user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="biz-logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <main className="biz-spotlight-main">
                {error && (
                    <div className="biz-error-banner">
                        <span>⚠️ {error}</span>
                        <button onClick={loadPosts} className="biz-retry-button">Retry</button>
                    </div>
                )}

                <div className="biz-create-section">
                    <button
                        className="biz-toggle-form-button"
                        onClick={() => setFormOpen((o) => !o)}
                    >
                        {formOpen ? '✕ Cancel' : '+ New Spotlight Post'}
                    </button>

                    {formOpen && (
                        <form className="biz-create-form" onSubmit={handleSubmit}>
                            <div className="biz-form-group">
                                <label>Title *</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Post title"
                                    required
                                    className="biz-input"
                                />
                            </div>

                            <div className="biz-form-group">
                                <label>Category *</label>
                                <select
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value as Category)}
                                    className="biz-select"
                                >
                                    <option value="tip">Tip</option>
                                    <option value="announcement">Announcement</option>
                                    <option value="feature">Feature</option>
                                    <option value="challenge">Challenge</option>
                                </select>
                            </div>

                            <div className="biz-form-group">
                                <label>Body *</label>
                                <textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    placeholder="Post content..."
                                    rows={5}
                                    required
                                    className="biz-textarea"
                                />
                            </div>

                            <div className="biz-form-group">
                                <label>Image URL (optional)</label>
                                <input
                                    type="url"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    placeholder="https://..."
                                    className="biz-input"
                                />
                            </div>

                            <button
                                type="submit"
                                className="biz-submit-button"
                                disabled={submitting || !title.trim() || !body.trim()}
                            >
                                {submitting ? 'Publishing...' : '✨ Publish Post'}
                            </button>
                        </form>
                    )}
                </div>

                {loading ? (
                    <div className="biz-loading">
                        <div className="biz-spinner"></div>
                        <p>Loading posts...</p>
                    </div>
                ) : posts.length === 0 ? (
                    <div className="biz-empty-state">
                        <div className="biz-empty-icon">✨</div>
                        <h3>No spotlight posts yet</h3>
                        <p>Create the first post above.</p>
                    </div>
                ) : (
                    <div className="biz-post-list">
                        {posts.map((post) => (
                            <div key={post.id} className="biz-post-card">
                                <div className="biz-post-header">
                                    <div className="biz-post-meta">
                                        <span className={`biz-category-badge biz-category-${post.category ?? 'tip'}`}>
                                            {post.category ?? 'unknown'}
                                        </span>
                                        <span className="biz-post-date">{formatDate(post.createdAt)}</span>
                                    </div>
                                    <button
                                        className="biz-delete-button"
                                        onClick={() => handleDelete(post.id)}
                                    >
                                        🗑 Delete
                                    </button>
                                </div>
                                <h3 className="biz-post-title">{post.title ?? '(No title)'}</h3>
                                <p className="biz-post-preview">{truncate(post.body, 200)}</p>
                                {post.imageUrl && (
                                    <a
                                        href={post.imageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="biz-image-link"
                                    >
                                        🖼 View Image
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default BizSpotlight;
